import React, { useEffect } from 'react';
import { Loader, Suspense } from '@axiom/ui';
import { useLocation, useNavigate, useParams } from 'react-router';

import { WindowUtil } from '../../utils/window-util';

import { SideDrawerContents } from './SideDrawerContents';

export type SideDrawerContainerProps = {
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
};

// Keyed by the route :id so the <Suspense> boundary remounts whenever a
// different record is selected, forcing its <Loader /> fallback to show again.
// React Router runs navigations inside a transition, which keeps the
// previously loaded content on screen (with no loader) while the next record
// loads — re-keying a brand-new boundary is the only way to surface the
// fallback on subsequent selections.
export const SideDrawerContainer = ({
  expanded,
  setExpanded,
}: SideDrawerContainerProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  // Open the drawer as soon as a record is routed to. This effect lives in the
  // container (parent of <Suspense>), not in SideDrawerContents — the latter
  // suspends on its API calls, so its effects can't fire until the data
  // resolves, making the drawer appear to do nothing on click.
  //
  // Keyed on `id` so it re-fires for *every* record routed to, not just the
  // first mount. Closing the drawer leaves the :id in the URL (close only
  // collapses via setExpanded(false)), so the container stays mounted; without
  // `id` here, selecting a different record after a close would never re-open
  // the drawer until its data finished loading. The container itself never
  // suspends, so this fires immediately on selection while the <Suspense>
  // boundary below shows its <Loader />.
  useEffect(() => {
    setExpanded(true);
  }, [setExpanded, id]);

  return (
    <Suspense
      key={id}
      stretched
      scrollable
      fallback={<Loader expand="cover" />}
    >
      <SideDrawerContents
        name="SIDE_DRAWER_CONTENTS"
        expanded={expanded}
        openDrawer={() => {
          setExpanded(true);
        }}
        closeDrawer={() => {
          setExpanded(false);
          void navigate(WindowUtil.appendQueryParams(pathname));
        }}
      />
    </Suspense>
  );
};
